import React, { useCallback, useMemo, useState } from 'react';
import {
  Accordion,
  AccordionDetails,
  AccordionSummary,
  Alert,
  Box,
  Button,
  Checkbox,
  FormControl,
  FormControlLabel,
  InputLabel,
  MenuItem,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import { incidentsApi } from '../../services/incidentsApi';
import { reportsApi, type SafetyDashboardRow } from '../../services/reportsApi';
import { MANAGED_CASE_OWNER_REQUIRED } from '../../domain/safetyCases/scheduledRunScanOutcome';
import { buildRiskPatternTitle } from '../../domain/safetyCases/riskPatternPortfolio';
import {
  buildDispositionParityNotes,
  dispositionParityFallbackRequired,
  dispositionParityInDrawer,
} from '../../domain/safetyCases/workspaceDispositionParity';

export type RiskPatternDispositionPayload = {
  create?: {
    title: string;
    owner: string;
    unassigned: boolean;
    rationale: string;
    confirmed: boolean;
  };
  notActionable?: { reasonCategory: string; reviewerNote: string };
  needsEvidence?: { reason: string; minimumEvidence: string; reviewDate: string };
  linkExisting?: { safetyCaseId: string; rationale: string };
};

export type LinkableSafetyCaseSource = {
  id: string;
  title: string;
  riskType?: string | null;
  zoneLabel?: string | null;
  siteId?: string | null;
};

export type LinkableSafetyCaseOption = {
  id: string;
  title: string;
  riskType?: string | null;
  zoneLabel?: string | null;
  siteId?: string | null;
};

export type RiskPatternDispositionKind =
  | 'create_safety_case'
  | 'not_actionable'
  | 'needs_more_evidence'
  | 'link_existing';

const NOT_ACTIONABLE_REASONS = [
  { value: 'expected_activity', label: 'Expected / authorized activity' },
  { value: 'false_positive', label: 'False positive / detector noise' },
  { value: 'duplicate', label: 'Duplicate of another reviewed pattern' },
  { value: 'out_of_scope', label: 'Outside managed safety scope' },
] as const;

function actorLabel(user?: { username?: string | null; email?: string | null } | null): string {
  return String(user?.username || user?.email || '').trim() || 'reviewer';
}

export function formatDispositionApiError(err: unknown): string {
  const anyErr = err as {
    message?: string;
    response?: { status?: number; data?: unknown };
  };
  const status = Number(anyErr?.response?.status || 0);
  const data = anyErr?.response?.data;
  let detail = '';
  if (data && typeof data === 'object') {
    const body = data as { detail?: unknown; error?: unknown };
    detail = String(body.detail || body.error || '').trim();
  } else if (typeof data === 'string') {
    detail = data.trim();
  }
  if (detail && !/^request failed with status code/i.test(detail) && detail.toLowerCase() !== '404 page not found') {
    return detail;
  }
  if (status === 404) {
    return (
      'Could not reach Safety Case create (HTTP 404). '
      + 'If the site was redeploying, wait until the dashboard is back, hard-refresh, then retry. '
      + 'If the case already appears under Safety Cases, no retry is needed.'
    );
  }
  if (status === 502 || status === 503) {
    return 'Safety Control API is temporarily unavailable. Wait a moment, refresh, then retry.';
  }
  const message = String(anyErr?.message || '').trim();
  if (message && !/^request failed with status code/i.test(message)) return message;
  if (status) return `Unable to record disposition (HTTP ${status}).`;
  return 'Unable to record disposition.';
}

async function auditRepresentativeDecision(args: {
  incidentId: string;
  status: 'CLOSED' | 'IN_REVIEW';
  caseDecision?: 'accepted' | 'rejected' | 'deferred' | 'already_managed' | 'excluded';
  reviewOutcome?: 'true_event' | 'false_positive' | 'expected_activity' | 'insufficient_evidence' | 'duplicate';
  closeReason?: 'TRUE_POSITIVE' | 'FALSE_POSITIVE' | 'NOT_ACTIONABLE' | 'DUPLICATE' | 'TEST';
  closeNote: string;
  priorStatus?: string | null;
}): Promise<void> {
  const incident = await incidentsApi.getIncident(args.incidentId);
  const expectedVersion = Number((incident as { version?: number })?.version || 1);
  const prior = String(args.priorStatus || (incident as { status?: string })?.status || 'OPEN').trim();
  await incidentsApi.patchStatus(
    args.incidentId,
    {
      status: args.status,
      case_decision: args.caseDecision,
      review_outcome: args.reviewOutcome,
      close_reason: args.closeReason,
      close_note: [
        args.closeNote,
        `prior_status=${prior}`,
        `source_interface=safety_control_workspace_drawer`,
      ].filter(Boolean).join(' | '),
    },
    { expected_version: expectedVersion },
  );
}

export function buildLinkableSafetyCaseOptions(
  row: SafetyDashboardRow | null,
  sources: LinkableSafetyCaseSource[],
): LinkableSafetyCaseOption[] {
  if (!row) return [];
  return matchLinkableSafetyCases(row, sources.filter((option) => option.id));
}

export function matchLinkableSafetyCases(
  row: SafetyDashboardRow,
  options: LinkableSafetyCaseOption[],
): LinkableSafetyCaseOption[] {
  const risk = String(row.risk_type || '').trim().toLowerCase();
  const site = String(row.site_id || '').trim().toLowerCase();
  const zone = String(row.zone_id || row.zone_label || row.area_label || '').trim().toLowerCase();
  return options.filter((option) => {
    if (!option.id) return false;
    if (site && option.siteId && String(option.siteId).trim().toLowerCase() !== site) return false;
    const optionRisk = String(option.riskType || '').trim().toLowerCase();
    if (risk && optionRisk && optionRisk !== risk) return false;
    if (risk && optionRisk && optionRisk === risk) return true;
    if (!risk) return true;
    const optionZone = String(option.zoneLabel || '').trim().toLowerCase();
    return Boolean(zone && optionZone && optionZone.includes(zone));
  });
}

export async function submitRiskPatternDisposition(args: {
  row: SafetyDashboardRow;
  kind: RiskPatternDispositionKind;
  actor: string;
  siteId?: string | null;
  reportFrom?: string | null;
  reportTo?: string | null;
  create?: {
    title: string;
    owner: string;
    unassigned: boolean;
    rationale: string;
    confirmed: boolean;
  };
  notActionable?: {
    reasonCategory: string;
    reviewerNote: string;
  };
  needsEvidence?: {
    reason: string;
    minimumEvidence: string;
    reviewDate: string;
  };
  linkExisting?: {
    safetyCaseId: string;
    rationale: string;
  };
}): Promise<{ ok: true; notice?: string } | { ok: false; message: string }> {
  const workflowId = String(args.row.workflow_id || '').trim();
  if (!workflowId) {
    return { ok: false, message: 'This recurring pattern is missing a workflow id.' };
  }
  const incidentId = String(args.row.representative_incident_id || '').trim();
  const actor = String(args.actor || '').trim() || 'reviewer';

  try {
    if (args.kind === 'create_safety_case') {
      const create = args.create;
      if (!create?.confirmed) {
        return { ok: false, message: 'Confirm that this recurring pattern should become a Safety Case.' };
      }
      const title = String(create.title || '').trim();
      if (!title) return { ok: false, message: 'Case title is required.' };
      const owner = String(create.owner || '').trim();
      if (!owner) {
        return { ok: false, message: MANAGED_CASE_OWNER_REQUIRED };
      }
      const saveResult = await reportsApi.saveSafetyFinding(workflowId, {
        representative_incident_id: incidentId || undefined,
        scope: {
          site_id: args.row.site_id || args.siteId || undefined,
          zones: args.row.zone_id ? [String(args.row.zone_id)] : [],
          risk_type: args.row.risk_type || undefined,
        },
        time_window: {
          start: args.reportFrom || undefined,
          end: args.reportTo || undefined,
        },
        source_pattern_id: workflowId,
        source_group_key: workflowId,
        hazard_category: args.row.risk_type || undefined,
        finding_type: args.row.risk_type || undefined,
        saved_by: actor,
        followup_owner: owner,
        followup_unassigned: false,
      });
      const alreadySaved = Boolean((saveResult as { already_saved?: boolean } | null)?.already_saved);
      if (incidentId) {
        try {
          await auditRepresentativeDecision({
            incidentId,
            status: 'IN_REVIEW',
            caseDecision: 'accepted',
            reviewOutcome: 'true_event',
            closeNote: [
              `Create Safety Case: ${title}`,
              owner ? `owner=${owner}` : '',
              create.rationale ? `rationale=${create.rationale}` : '',
              `actor=${actor}`,
            ].filter(Boolean).join(' | '),
          });
        } catch {
          // Case create is authoritative; do not surface a hard failure if audit is unavailable.
          return {
            ok: true,
            notice: alreadySaved
              ? 'Safety Case already existed. Evidence audit could not be recorded — use Review Evidence if needed.'
              : 'Safety Case created. Evidence audit could not be recorded — use Review Evidence if needed.',
          };
        }
      }
      return {
        ok: true,
        notice: alreadySaved ? 'Safety Case already existed and was resumed.' : 'Safety Case created.',
      };
    }

    if (!incidentId) {
      return {
        ok: false,
        message: 'Representative evidence is required for this disposition. Use Review Evidence to finish in full review.',
      };
    }

    if (args.kind === 'not_actionable') {
      const reasonCategory = String(args.notActionable?.reasonCategory || '').trim();
      const reviewerNote = String(args.notActionable?.reviewerNote || '').trim();
      if (!reasonCategory) return { ok: false, message: 'Select a reason category.' };
      if (!reviewerNote) return { ok: false, message: 'A reviewer note is required.' };
      const reviewOutcome = (
        reasonCategory === 'false_positive' || reasonCategory === 'duplicate'
          ? reasonCategory
          : 'expected_activity'
      ) as 'false_positive' | 'duplicate' | 'expected_activity';
      await auditRepresentativeDecision({
        incidentId,
        status: 'CLOSED',
        caseDecision: 'rejected',
        reviewOutcome,
        closeReason: reviewOutcome === 'false_positive'
          ? 'FALSE_POSITIVE'
          : reviewOutcome === 'duplicate'
            ? 'DUPLICATE'
            : 'NOT_ACTIONABLE',
        closeNote: [
          `Not actionable (${reasonCategory})`,
          `note=${reviewerNote}`,
          `actor=${actor}`,
        ].join(' | '),
      });
      return { ok: true, notice: 'Marked not actionable.' };
    }

    if (args.kind === 'needs_more_evidence') {
      const reason = String(args.needsEvidence?.reason || '').trim();
      if (!reason) return { ok: false, message: 'A reason is required.' };
      const minimumEvidence = String(args.needsEvidence?.minimumEvidence || '').trim();
      const reviewDate = String(args.needsEvidence?.reviewDate || '').trim();
      await auditRepresentativeDecision({
        incidentId,
        status: 'IN_REVIEW',
        caseDecision: 'deferred',
        reviewOutcome: 'insufficient_evidence',
        closeNote: [
          `Needs more evidence: ${reason}`,
          minimumEvidence ? `minimum_evidence=${minimumEvidence}` : '',
          reviewDate ? `review_date=${reviewDate}` : '',
          `actor=${actor}`,
        ].filter(Boolean).join(' | '),
      });
      return { ok: true, notice: 'Requested more evidence.' };
    }

    const safetyCaseId = String(args.linkExisting?.safetyCaseId || '').trim();
    const rationale = String(args.linkExisting?.rationale || '').trim();
    if (!safetyCaseId) return { ok: false, message: 'Select an existing Safety Case.' };
    if (!rationale) return { ok: false, message: 'Explain why this pattern links to that case.' };
    await auditRepresentativeDecision({
      incidentId,
      status: 'CLOSED',
      caseDecision: 'already_managed',
      reviewOutcome: 'expected_activity',
      closeReason: 'NOT_ACTIONABLE',
      closeNote: [
        `Link existing Safety Case ${safetyCaseId}`,
        `rationale=${rationale}`,
        `actor=${actor}`,
      ].join(' | '),
    });
    return { ok: true, notice: 'Linked to existing Safety Case.' };
  } catch (err: unknown) {
    return { ok: false, message: formatDispositionApiError(err) };
  }
}

export function useRiskPatternDrawerDisposition(args: {
  row: SafetyDashboardRow | null;
  linkSources: LinkableSafetyCaseSource[];
  actorUser?: { username?: string | null; email?: string | null } | null;
  siteId?: string | null;
  reportFrom?: string | null;
  reportTo?: string | null;
  onCloseDrawer: () => void;
  onRefresh: () => Promise<void>;
  onGlobalError?: (message: string | null) => void;
}) {
  const {
    row,
    linkSources,
    actorUser,
    siteId,
    reportFrom,
    reportTo,
    onCloseDrawer,
    onRefresh,
    onGlobalError,
  } = args;
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const linkableCases = useMemo(
    () => buildLinkableSafetyCaseOptions(row, linkSources),
    [linkSources, row],
  );

  const resetDisposition = useCallback(() => {
    setError(null);
    setBusy(false);
  }, []);

  const submit = useCallback(async (
    kind: RiskPatternDispositionKind,
    payload: RiskPatternDispositionPayload,
  ) => {
    if (!row) return;
    setBusy(true);
    setError(null);
    onGlobalError?.(null);
    const result = await submitRiskPatternDisposition({
      row,
      kind,
      actor: String(actorUser?.username || actorUser?.email || '').trim() || 'reviewer',
      siteId,
      reportFrom,
      reportTo,
      create: payload.create,
      notActionable: payload.notActionable,
      needsEvidence: payload.needsEvidence,
      linkExisting: payload.linkExisting,
    });
    if (!result.ok) {
      setError(result.message);
      setBusy(false);
      return;
    }
    onCloseDrawer();
    setBusy(false);
    await onRefresh();
  }, [
    actorUser?.email,
    actorUser?.username,
    onCloseDrawer,
    onGlobalError,
    onRefresh,
    reportFrom,
    reportTo,
    row,
    siteId,
  ]);

  return {
    busy,
    error,
    linkableCases,
    resetDisposition,
    submit,
    setError,
  };
}

export function RiskPatternDispositionPanel({
  row,
  canDispose,
  busy,
  linkableCases,
  actorUser,
  error,
  onReviewEvidence,
  onSubmit,
}: {
  row: SafetyDashboardRow;
  canDispose: boolean;
  busy: boolean;
  linkableCases: LinkableSafetyCaseOption[];
  actorUser?: { username?: string | null; email?: string | null } | null;
  error?: string | null;
  onReviewEvidence: () => void;
  onSubmit: (kind: RiskPatternDispositionKind, payload: {
    create?: {
      title: string;
      owner: string;
      unassigned: boolean;
      rationale: string;
      confirmed: boolean;
    };
    notActionable?: { reasonCategory: string; reviewerNote: string };
    needsEvidence?: { reason: string; minimumEvidence: string; reviewDate: string };
    linkExisting?: { safetyCaseId: string; rationale: string };
  }) => void;
}) {
  const [active, setActive] = useState<RiskPatternDispositionKind | null>(null);
  const defaultTitle = useMemo(() => buildRiskPatternTitle(row), [row]);
  const [title, setTitle] = useState(defaultTitle);
  const [owner, setOwner] = useState(String(row.followup_owner || row.owner_name || row.owner || '').trim());
  const [rationale, setRationale] = useState('');
  const [confirmed, setConfirmed] = useState(false);
  const [reasonCategory, setReasonCategory] = useState<string>(NOT_ACTIONABLE_REASONS[0].value);
  const [reviewerNote, setReviewerNote] = useState('');
  const [evidenceReason, setEvidenceReason] = useState('');
  const [minimumEvidence, setMinimumEvidence] = useState('');
  const [reviewDate, setReviewDate] = useState('');
  const [linkCaseId, setLinkCaseId] = useState(linkableCases[0]?.id || '');
  const [linkRationale, setLinkRationale] = useState('');
  const reviewerIdentity = actorLabel(actorUser);
  const parityNotes = useMemo(() => buildDispositionParityNotes(), []);
  const inDrawerParity = useMemo(() => dispositionParityInDrawer(parityNotes), [parityNotes]);
  const fallbackParity = useMemo(() => dispositionParityFallbackRequired(parityNotes), [parityNotes]);

  const start = (kind: RiskPatternDispositionKind) => {
    setActive(kind);
    if (kind === 'create_safety_case') {
      setTitle(defaultTitle);
      setConfirmed(false);
    }
  };

  const submitActive = () => {
    if (!active) return;
    if (active === 'create_safety_case') {
      onSubmit(active, {
        create: { title, owner, unassigned: false, rationale, confirmed },
      });
      return;
    }
    if (active === 'not_actionable') {
      onSubmit(active, {
        notActionable: { reasonCategory, reviewerNote },
      });
      return;
    }
    if (active === 'needs_more_evidence') {
      onSubmit(active, {
        needsEvidence: { reason: evidenceReason, minimumEvidence, reviewDate },
      });
      return;
    }
    onSubmit(active, {
      linkExisting: { safetyCaseId: linkCaseId, rationale: linkRationale },
    });
  };

  return (
    <Box data-testid="risk-pattern-disposition-panel">
      <Typography variant="subtitle2" sx={{ fontWeight: 800, mb: 0.75 }}>
        Decision
      </Typography>
      <Typography variant="body2" sx={{ mb: 0.75 }}>
        Does this recurring pattern require management as a Safety Case?
      </Typography>
      <Typography
        variant="caption"
        color="text.secondary"
        display="block"
        sx={{ mb: 1.5 }}
        data-testid="disposition-primary-surface-note"
      >
        {parityNotes.summary}
      </Typography>

      {!canDispose ? (
        <Alert severity="info" sx={{ mb: 1.5 }}>
          Viewers can inspect this pattern. Disposition requires Reviewer access.
        </Alert>
      ) : null}

      {error ? (
        <Alert severity="error" sx={{ mb: 1.5 }} data-testid="risk-pattern-disposition-error">
          {error}
        </Alert>
      ) : null}

      <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75, fontWeight: 700 }}>
        Primary disposition
      </Typography>
      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        flexWrap="wrap"
        sx={{ mb: 1.5 }}
        data-testid="disposition-primary-actions"
      >
        <Button
          variant="contained"
          disabled={!canDispose || busy}
          onClick={() => start('create_safety_case')}
          data-testid="disposition-create-safety-case"
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Create Safety Case
        </Button>
        <Button
          variant="outlined"
          disabled={!canDispose || busy}
          onClick={() => start('not_actionable')}
          data-testid="disposition-not-actionable"
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Not actionable
        </Button>
        <Button
          variant="outlined"
          disabled={!canDispose || busy}
          onClick={() => start('needs_more_evidence')}
          data-testid="disposition-needs-more-evidence"
          sx={{ textTransform: 'none', fontWeight: 700 }}
        >
          Needs more evidence
        </Button>
        {linkableCases.length > 0 ? (
          <Button
            variant="outlined"
            disabled={!canDispose || busy}
            onClick={() => start('link_existing')}
            data-testid="disposition-link-existing"
            sx={{ textTransform: 'none', fontWeight: 700 }}
          >
            Link existing
          </Button>
        ) : null}
      </Stack>

      {active === 'create_safety_case' ? (
        <Stack spacing={1.25} data-testid="disposition-form-create">
          <TextField
            label="Case title"
            value={title}
            onChange={(event) => setTitle(event.target.value)}
            size="small"
            fullWidth
            required
          />
          <TextField
            label="Case owner"
            value={owner}
            onChange={(event) => setOwner(event.target.value)}
            size="small"
            fullWidth
            required
            helperText="Required. Person accountable for this Safety Case."
            inputProps={{ 'data-testid': 'disposition-create-owner' }}
          />
          <TextField
            label="Reviewer rationale (optional)"
            value={rationale}
            onChange={(event) => setRationale(event.target.value)}
            size="small"
            fullWidth
            multiline
            minRows={2}
          />
          <FormControlLabel
            control={(
              <Checkbox
                checked={confirmed}
                onChange={(event) => setConfirmed(event.target.checked)}
                data-testid="disposition-create-confirm"
              />
            )}
            label="I confirm this recurring pattern should be managed as a Safety Case."
          />
          <Button
            variant="contained"
            disabled={busy}
            onClick={submitActive}
            data-testid="disposition-submit-create"
            sx={{ textTransform: 'none', fontWeight: 700, alignSelf: 'flex-start' }}
          >
            Confirm Create Safety Case
          </Button>
        </Stack>
      ) : null}

      {active === 'not_actionable' ? (
        <Stack spacing={1.25} data-testid="disposition-form-not-actionable">
          <FormControl size="small" fullWidth>
            <InputLabel id="disposition-reason-category-label">Reason category</InputLabel>
            <Select
              labelId="disposition-reason-category-label"
              label="Reason category"
              value={reasonCategory}
              onChange={(event) => setReasonCategory(String(event.target.value))}
            >
              {NOT_ACTIONABLE_REASONS.map((option) => (
                <MenuItem key={option.value} value={option.value}>{option.label}</MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Reviewer note"
            value={reviewerNote}
            onChange={(event) => setReviewerNote(event.target.value)}
            size="small"
            fullWidth
            required
            multiline
            minRows={2}
          />
          <Typography variant="caption" color="text.secondary">
            Reviewer: {reviewerIdentity}
          </Typography>
          <Button
            variant="contained"
            disabled={busy}
            onClick={submitActive}
            data-testid="disposition-submit-not-actionable"
            sx={{ textTransform: 'none', fontWeight: 700, alignSelf: 'flex-start' }}
          >
            Confirm not actionable
          </Button>
        </Stack>
      ) : null}

      {active === 'needs_more_evidence' ? (
        <Stack spacing={1.25} data-testid="disposition-form-needs-evidence">
          <TextField
            label="Reason"
            value={evidenceReason}
            onChange={(event) => setEvidenceReason(event.target.value)}
            size="small"
            fullWidth
            required
            multiline
            minRows={2}
          />
          <TextField
            label="Minimum evidence request (optional)"
            value={minimumEvidence}
            onChange={(event) => setMinimumEvidence(event.target.value)}
            size="small"
            fullWidth
          />
          <TextField
            label="Review date (optional)"
            type="date"
            value={reviewDate}
            onChange={(event) => setReviewDate(event.target.value)}
            size="small"
            fullWidth
            InputLabelProps={{ shrink: true }}
          />
          <Button
            variant="contained"
            disabled={busy}
            onClick={submitActive}
            data-testid="disposition-submit-needs-evidence"
            sx={{ textTransform: 'none', fontWeight: 700, alignSelf: 'flex-start' }}
          >
            Request more evidence
          </Button>
        </Stack>
      ) : null}

      {active === 'link_existing' ? (
        <Stack spacing={1.25} data-testid="disposition-form-link-existing">
          <FormControl size="small" fullWidth>
            <InputLabel id="disposition-link-case-label">Existing Safety Case</InputLabel>
            <Select
              labelId="disposition-link-case-label"
              label="Existing Safety Case"
              value={linkCaseId}
              onChange={(event) => setLinkCaseId(String(event.target.value))}
            >
              {linkableCases.map((option) => (
                <MenuItem key={option.id} value={option.id}>
                  {option.title}
                </MenuItem>
              ))}
            </Select>
          </FormControl>
          <TextField
            label="Why this pattern belongs to that case"
            value={linkRationale}
            onChange={(event) => setLinkRationale(event.target.value)}
            size="small"
            fullWidth
            required
            multiline
            minRows={2}
          />
          <Button
            variant="contained"
            disabled={busy}
            onClick={submitActive}
            data-testid="disposition-submit-link-existing"
            sx={{ textTransform: 'none', fontWeight: 700, alignSelf: 'flex-start' }}
          >
            Link to existing Safety Case
          </Button>
        </Stack>
      ) : null}

      <Box
        data-testid="disposition-fallback-section"
        sx={{
          mt: 1.5,
          pt: 1.5,
          borderTop: '1px solid',
          borderColor: 'divider',
        }}
      >
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 0.75, fontWeight: 700 }}>
          Full review fallback
        </Typography>
        <Typography variant="caption" color="text.secondary" display="block" sx={{ mb: 1 }}>
          {parityNotes.fallbackSurfaceLabel} stays available until media and observation-level review reach drawer parity.
        </Typography>
        <Button
          variant="text"
          size="small"
          disabled={busy}
          onClick={onReviewEvidence}
          data-testid="risk-pattern-drawer-review-evidence"
          sx={{ textTransform: 'none', fontWeight: 700, px: 0, mb: 1 }}
        >
          {parityNotes.fallbackCtaLabel}
        </Button>
        <Accordion
          disableGutters
          elevation={0}
          data-testid="disposition-parity-notes"
          sx={{
            border: '1px solid',
            borderColor: 'divider',
            borderRadius: '8px !important',
            bgcolor: 'transparent',
            '&:before': { display: 'none' },
          }}
        >
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography variant="caption" sx={{ fontWeight: 700 }}>
              Parity notes — drawer vs /incidents
            </Typography>
          </AccordionSummary>
          <AccordionDetails sx={{ pt: 0 }}>
            <Stack spacing={1.25}>
              <Box data-testid="disposition-parity-in-drawer">
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Covered in {parityNotes.primarySurfaceLabel}
                </Typography>
                <Stack spacing={0.75} component="ul" sx={{ m: 0, pl: 2 }}>
                  {inDrawerParity.map((item) => (
                    <Typography key={item.id} component="li" variant="caption" color="text.secondary">
                      <strong>{item.label}.</strong> {item.detail}
                    </Typography>
                  ))}
                </Stack>
              </Box>
              <Box data-testid="disposition-parity-fallback-required">
                <Typography variant="caption" color="text.secondary" display="block" sx={{ fontWeight: 700, mb: 0.5 }}>
                  Still requires {parityNotes.fallbackSurfaceLabel}
                </Typography>
                <Stack spacing={0.75} component="ul" sx={{ m: 0, pl: 2 }}>
                  {fallbackParity.map((item) => (
                    <Typography key={item.id} component="li" variant="caption" color="text.secondary">
                      <strong>{item.label}.</strong> {item.detail}
                    </Typography>
                  ))}
                </Stack>
              </Box>
            </Stack>
          </AccordionDetails>
        </Accordion>
      </Box>
    </Box>
  );
}

export default RiskPatternDispositionPanel;
